import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  TextField,
  MuiToggleButton as ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "components/mui";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import { Link as RouterLink } from "react-router-dom";
import { ROUTE_MAIL_LIST, ROUTE_EMAIL_HISTORY } from "constants/routerPath";
import SurveyInserterPanel from "../SurveyInserterPanel";
import RecipientPicker from "components/RecipientPicker";

/**
 * The left-hand composer card: recipient list, From/Reply-to/Subject, the editor
 * toggle, template loading, and the survey panel.
 *
 * Presentational — all state lives in the composer's hooks.
 */
function ComposerFields({
  mailLists,
  selectedTo,
  onSelectedToChange,
  recipientMode = "list",
  onRecipientModeChange,
  adHocRecipients = [],
  onAdHocRecipientsChange,

  emailPrefix,
  domain,
  isPrefixLocked,
  onPrefixChange,
  onPrefixBlur,
  onTogglePrefixLock,

  replyTo,
  onReplyToChange,
  subject,
  onSubjectChange,

  isDropEditor,
  canUseDropEditor,
  onEditorModeChange,
  hasUneditableDropContent,
  onLoadTemplateClick,

  survey,
}) {
  return (
    <Paper
      elevation={2}
      sx={{
        p: 3,
        borderRadius: 2,
        display: "flex",
        flexDirection: "column",
        gap: 2.5,
        width: "100%",
        height: "100%",
      }}
    >
      <Box display="flex" flexDirection="column" gap={1.5}>
        <ToggleButtonGroup
          value={recipientMode}
          exclusive
          size="small"
          onChange={(event, newMode) => {
            if (newMode !== null) onRecipientModeChange?.(newMode);
          }}
          aria-label="recipient source"
        >
          <ToggleButton value="list">Mailing list</ToggleButton>
          <ToggleButton value="adhoc">Custom recipients</ToggleButton>
        </ToggleButtonGroup>

        {recipientMode === "adhoc" ? (
          <RecipientPicker
            value={adHocRecipients}
            onChange={onAdHocRecipientsChange}
            label="Recipients"
          />
        ) : (
          <FormControl sx={{ flex: 1 }}>
            <InputLabel id="to-label">To</InputLabel>
            <Select
              labelId="to-label"
              id="to-select"
              label="To"
              value={selectedTo}
              onChange={(e) => onSelectedToChange(e.target.value)}
            >
              {mailLists.map((list) => (
                <MenuItem key={list.list_id} value={list.list_id}>
                  {list.list_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      <Box>
        <Button
          variant="outlined"
          color="primary"
          sx={{ mr: 2 }}
          component={RouterLink}
          to={ROUTE_MAIL_LIST}
        >
          List Manager
        </Button>

        <Tooltip title="View email history" arrow>
          <Chip
            label="Email History"
            variant="outlined"
            color="primary"
            sx={{
              my: 1,
              cursor: "pointer",
              opacity: 1,
              pointerEvents: "auto",
            }}
            icon={<MarkEmailReadIcon />}
            component={RouterLink}
            to={ROUTE_EMAIL_HISTORY}
            clickable
          />
        </Tooltip>
      </Box>

      {/* Auto populated Email From field */}
      <FormControl variant="outlined" fullWidth>
        <InputLabel id="from-label">From</InputLabel>
        <OutlinedInput
          id="from-prefix"
          label="From"
          value={emailPrefix}
          onChange={(e) => onPrefixChange(e.target.value)}
          onBlur={onPrefixBlur}
          disabled={isPrefixLocked}
          endAdornment={
            <>
              <InputAdornment
                position="end"
                sx={{
                  mr: 0,
                  color: "text.secondary",
                  pointerEvents: "none",
                  userSelect: "none",
                  fontWeight: 400,
                  fontSize: "1rem",
                  "&::before": {
                    content: `"${domain ? `@${domain}` : ""}"`,
                  },
                }}
              />
              <InputAdornment position="end">
                <Tooltip
                  title={
                    isPrefixLocked
                      ? "Unlock email prefix for editing"
                      : "Lock email prefix"
                  }
                >
                  <IconButton
                    edge="end"
                    onClick={onTogglePrefixLock}
                    aria-label={
                      isPrefixLocked ? "unlock email prefix" : "lock email prefix"
                    }
                  >
                    {isPrefixLocked ? <LockIcon /> : <LockOpenIcon />}
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            </>
          }
          inputProps={{
            "aria-label": "email prefix",
          }}
          sx={{
            "& .MuiOutlinedInput-input": {
              paddingRight: 0, // Allow more space for domain text
            },
          }}
        />
      </FormControl>

      <Tooltip
        title="Optional: Set one or more reply-to addresses. For multiple addresses, use comma-separated format (e.g., 'support@example.com, sales@example.com'). If left blank, defaults to the 'From' address."
        placement="right"
      >
        <TextField
          fullWidth
          label="Reply-to (optional)"
          variant="outlined"
          value={replyTo}
          onChange={(e) => onReplyToChange(e.target.value)}
          type="text"
          autoComplete="off"
        />
      </Tooltip>

      <TextField
        fullWidth
        label="Subject"
        variant="outlined"
        value={subject}
        onChange={(e) => onSubjectChange(e.target.value)}
      />

      <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
        {/* The toggle is hidden entirely below the Drop-editor width — mobile is
            single-editor-only, so offering a choice that cannot work is worse
            than offering none. */}
        {canUseDropEditor && (
          <ToggleButtonGroup
            value={isDropEditor ? "drop" : "text"}
            exclusive
            onChange={(event, newValue) => {
              if (newValue !== null) {
                onEditorModeChange(newValue === "drop");
              }
            }}
            size="small"
          >
            <ToggleButton value="drop">Drop Editor</ToggleButton>
            <ToggleButton value="text">Text Editor</ToggleButton>
          </ToggleButtonGroup>
        )}
        {/* Templates work in both editors — `editor_type` records which one a
            template was authored in, and LoadTemplateModal switches the composer
            to match. Gating this on isDropEditor removed template access from
            every non-Drop user, including all of mobile. */}
        <Button variant="contained" component="label" onClick={onLoadTemplateClick}>
          Load Template
        </Button>
      </Box>

      {/* A Drop design exists but cannot be edited at this width. Tell the user
          rather than silently showing them an empty text editor. */}
      {hasUneditableDropContent && (
        <Alert severity="warning">
          This email was built in the Drop editor, which needs a wider screen. Your
          design is safe, but it can't be edited or sent here — reopen this page on a
          desktop.
        </Alert>
      )}

      <Box display="flex" alignItems="center" gap={2}>
        {!survey.showPanel && (
          <Button variant="outlined" color="primary" onClick={survey.showPanelAndLoad}>
            Insert Survey
          </Button>
        )}
      </Box>

      {/* Survey Inserter Panel */}
      {survey.showPanel && (
        <Box sx={{ mt: 2 }}>
          <SurveyInserterPanel
            surveys={survey.surveys}
            selectedSurveyId={survey.selectedSurveyId}
            loading={survey.loading}
            onSurveyChange={survey.handleSurveyChange}
            onEdit={survey.handleEdit}
            onCreateNew={survey.handleCreateNew}
            onCancel={survey.handleCancel}
            onInsert={survey.handleInsert}
          />
        </Box>
      )}
    </Paper>
  );
}

export default ComposerFields;
